import React, { useState } from "react";

import { Dialog } from "../Dialog";
import { t } from "../../i18n";
import type { PremiumLibraryItem } from "../../data/premiumLibraryData";
import { activatePremium, togglePremiumForDemo } from "../../data/premiumAuth";

interface PurchasePromptProps {
  isOpen: boolean;
  onClose: () => void;
  item?: PremiumLibraryItem;
  onPurchaseComplete?: () => void;
}

export const PurchasePrompt: React.FC<PurchasePromptProps> = ({
  isOpen,
  onClose,
  item,
  onPurchaseComplete,
}) => {
  const [licenseKey, setLicenseKey] = useState("");
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState("");

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      setError("Please enter a license key");
      return;
    }

    setIsActivating(true);
    setError("");

    try {
      const success = activatePremium(licenseKey.trim());
      if (success) {
        onPurchaseComplete?.();
        onClose();
        setLicenseKey("");
      } else {
        setError("Invalid license key. Please check and try again.");
      }
    } catch (err) {
      setError("Failed to activate license. Please try again.");
    } finally {
      setIsActivating(false);
    }
  };

  const handleDemoActivation = () => {
    togglePremiumForDemo();
    onPurchaseComplete?.();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog
      onCloseRequest={onClose}
      title="Premium Feature"
      className="purchase-prompt-dialog"
    >
      <div className="purchase-prompt-content">
        <div className="purchase-prompt-item">
          {item && (
            <>
              <h3 className="purchase-prompt-item__name">{item.name}</h3>
              <p className="purchase-prompt-item__description">
                This is a premium library item. Upgrade to Pro to access all premium elements and unlock your creativity.
              </p>
            </>
          )}
        </div>

        <div className="purchase-prompt-features">
          <h4>Premium Library includes:</h4>
          <ul>
            <li>✨ Advanced UI components</li>
            <li>🎨 Styled elements and templates</li>
            <li>📝 Form elements and inputs</li>
            <li>🔘 Professional button variations</li>
            <li>🧭 Navigation components</li>
            <li>🚀 Regular updates with new elements</li>
          </ul>
        </div>

        <div className="purchase-prompt-actions">
          <div className="purchase-prompt-license">
            <h4>Already have a license?</h4>
            <div className="purchase-prompt-license__input-group">
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                placeholder="Enter your license key"
                className="purchase-prompt-license__input"
                disabled={isActivating}
              />
              <button
                onClick={handleActivate}
                disabled={isActivating || !licenseKey.trim()}
                className="purchase-prompt-license__activate-btn"
              >
                {isActivating ? "Activating..." : "Activate"}
              </button>
            </div>
            {error && (
              <div className="purchase-prompt-license__error">{error}</div>
            )}
            <div className="purchase-prompt-license__demo">
              <p>For demo purposes, try these license keys:</p>
              <code>PREMIUM-2024-VALID</code>, <code>LOFI-PRO-LICENSE</code>, or <code>DEVELOPER-TEST-KEY</code>
            </div>
          </div>

          <div className="purchase-prompt-demo">
            <h4>Just testing?</h4>
            <button
              onClick={handleDemoActivation}
              className="purchase-prompt-demo__btn"
            >
              Toggle Demo Mode
            </button>
            <p className="purchase-prompt-demo__note">
              This will temporarily enable/disable premium features for demonstration.
            </p>
          </div>

          <div className="purchase-prompt-purchase">
            <h4>Need a license?</h4>
            <a
              href="https://lofi.com/premium"
              target="_blank"
              rel="noopener noreferrer"
              className="purchase-prompt-purchase__btn"
            >
              Get Premium License
            </a>
            <p className="purchase-prompt-purchase__note">
              One-time purchase • Lifetime access • 30-day money-back guarantee
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}; 